// Programs-list logic shared by every surface that shows the caller's
// programs (mobile Plan tab, web-rnw console list). Pure TS — the React
// hook lives in @heraion/ui (usePrograms); this module owns the
// parse + classification rules so they can't drift between apps
// (anti-monster S-B).

import {
  Program,
  ProgramProgress,
  ProgramSummary,
  type Program as ProgramT,
  type ProgramProgress as ProgramProgressT,
  type ProgramStatus,
  type ProgramSummary as ProgramSummaryT,
} from "./schemas/program.js";

/** #311: THE definition of "followed", the one every surface uses: the
 *  reader's own row is `active`, or a FOLLOW#program#<id> row stands in the
 *  reader's partition. The server computes it where both facts are visible
 *  (GET /api/me/programs stamps `followed` on each row; the pin clear in
 *  apps/api lib/programPin.ts); the athlete's own client feeds it the follow
 *  ids it already holds (splitProgramsByStatus). Who WROTE the row says
 *  nothing: a legacy copy is written by the coach, a CO-22 alter ego follows
 *  a row of its own. */
export const isFollowedProgram = (
  ownStatus: ProgramStatus | undefined,
  hasFollowRow: boolean,
): boolean => hasFollowRow || ownStatus === "active";

/** #448: what a program is to its reader, in words, since `status` alone
 *  misleads (a followed program can read `archived`, its author's status).
 *  followed: In progress. mine: among my programs, not followed. hidden: no
 *  longer in use. Followed wins: a follow is never hidden by its author. */
export type ProgramStanding = "followed" | "mine" | "hidden";

export const programStanding = (
  ownStatus: ProgramStatus | undefined,
  followed: boolean,
): ProgramStanding =>
  followed ? "followed" : ownStatus === "hidden" ? "hidden" : "mine";

/** #537: the reader's relation to a program, a READ-time field derived from
 *  programStanding, never written on the row. hidden maps to unfollowed. */
export type ProgramRelation = "followed" | "mine" | "unfollowed";

export const programRelation = (standing: ProgramStanding): ProgramRelation =>
  standing === "hidden" ? "unfollowed" : standing;

/** #448: THE «hidden» test, out of the Library and every list. A program
 *  followed through a follow row is never hidden by its author's status. */
export const isHiddenProgram = (
  ownStatus: ProgramStatus | undefined,
  hasFollowRow: boolean,
): boolean =>
  programStanding(ownStatus, isFollowedProgram(ownStatus, hasFollowRow)) === "hidden";

/** Parse the GET /api/me/programs payload. Per-item zod validation:
 *  one malformed row is dropped instead of poisoning the whole list
 *  (same posture as the catalog and feedback parsers). */
export const parseProgramsList = (raw: unknown): ProgramT[] => {
  if (!Array.isArray(raw)) return [];
  const out: ProgramT[] = [];
  for (const item of raw) {
    const parsed = Program.safeParse(item);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
};

/** A row of a coach's read of an athlete's programs: the full program, or
 *  only its summary when another coach wrote it (#332). `followed` is the
 *  server's verdict (isFollowedProgram), a read-only stamp that lives on no
 *  write schema; absent from an older API or cache. */
export type ProgramRow = (ProgramT | ProgramSummaryT) & {
  followed?: boolean;
  /** #448: the server's words for the same verdict. */
  standing?: ProgramStanding;
  /** #537: followed | mine | unfollowed, derived at read time. */
  relation?: ProgramRelation;
};

export const isFullProgram = (p: ProgramRow): p is ProgramT => "sessions" in p;

/** The athlete's pinned program as a coach gets it: `row` names it,
 *  `program` is set only when its content is readable (not a summary).
 *  #311: a pin counts only while it names a program the athlete follows,
 *  so a stale pin (left on an unfollowed program) reads as "no block".
 *  The row's `followed` stamp decides; without it (an older API or cache)
 *  only an `active` row counts. */
export const findPinnedProgram = (
  programs: ReadonlyArray<ProgramRow>,
  currentProgramId: string | undefined,
): { row: ProgramRow | null; program: ProgramT | null } => {
  const found = currentProgramId
    ? programs.find((p) => p.id === currentProgramId)
    : undefined;
  const row =
    found && (found.followed ?? isFollowedProgram(found.status, false))
      ? found
      : null;
  return { row, program: row && isFullProgram(row) ? row : null };
};

// The server's `followed` stamp, carried past the zod parse that strips it.
const withFollowed = <T extends object>(
  row: T,
  raw: unknown,
): T & { followed?: boolean } => {
  const flag = (raw as { followed?: unknown }).followed;
  return typeof flag === "boolean" ? { ...row, followed: flag } : row;
};

/** Parse GET /api/me/programs?athleteSub=. Keeps the summary rows that
 *  parseProgramsList drops: the coach can name those programs, not open
 *  them. A malformed row is dropped, as there. */
export const parseProgramSummaries = (raw: unknown): ProgramRow[] => {
  if (!Array.isArray(raw)) return [];
  const out: ProgramRow[] = [];
  for (const item of raw) {
    const full = Program.safeParse(item);
    if (full.success) {
      out.push(withFollowed(full.data, item));
      continue;
    }
    const summary = ProgramSummary.safeParse(item);
    if (summary.success) out.push(withFollowed(summary.data, item));
  }
  return out;
};

/** Parse one GET /api/me/programs/:id/progress payload. null means
 *  "never run" (the server returns null) or schema drift. */
export const parseProgramProgress = (raw: unknown): ProgramProgressT | null => {
  if (raw === null || raw === undefined) return null;
  const parsed = ProgramProgress.safeParse(raw);
  return parsed.success ? parsed.data : null;
};

/** The stored statuses (#379: Publish is gone, a new program is born
 *  `archived`). UI words:
 *    - followed  (status=active):   "In corso", the only startable bucket.
 *    - mine      (status=archived): created or kept, not followed.
 *    - drafts    (status=draft):    old rows only; they can be followed
 *                                   directly ("Bozza" label stays).
 *    - hidden    (status=hidden):   in no bucket (#448). */
export const splitProgramsByStatus = (
  programs: ReadonlyArray<ProgramT>,
  /** Ids of programs the caller FOLLOWS: a pointer to a row that lives in
   *  its author's partition (next/coaching.md, "La SPEC del follower" §2).
   *  Their `status` is the AUTHOR's, so it says nothing about the follower:
   *  a program the author left "archived" would land under "mine". The
   *  follow decides the bucket instead, and it is always "followed".
   *  Empty (the default) ⇒ every program is classified by status exactly as
   *  before. */
  followedIds: ReadonlySet<string> = new Set(),
): { followed: ProgramT[]; published: ProgramT[]; drafts: ProgramT[] } => {
  const followed: ProgramT[] = [];
  const published: ProgramT[] = [];
  const drafts: ProgramT[] = [];
  for (const p of programs) {
    // A followed pointer is always in followedIds, so its (author's)
    // status never decides here.
    const hasFollow = followedIds.has(p.id);
    if (isHiddenProgram(p.status, hasFollow)) continue;
    if (isFollowedProgram(p.status, hasFollow)) followed.push(p);
    else if (p.status === "archived") published.push(p);
    else if (p.status === "draft") drafts.push(p);
  }
  return { followed, published, drafts };
};
